// About.js - Updated with accurate business timeline and future plans
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const About = () => {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    const elements = document.querySelectorAll('.reveal-on-scroll');
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // Updated stats reflecting actual business status
  const stats = [
    { value: '2021', label: 'Year Founded (Aalitech)', icon: 'fas fa-calendar-alt', color: '#FF6B35' },
    { value: '8+', label: 'Future Ventures Planned', icon: 'fas fa-chart-line', color: '#3B82F6' },
    { value: '3+', label: 'Operational Offices', icon: 'fas fa-building', color: '#10B981' },
    { value: '100+', label: 'Team Members', icon: 'fas fa-users', color: '#8B5CF6' },
  ];

  // Updated timeline with accurate actual and planned milestones
  const milestones = [
    { year: '2021', title: 'Aalitech Established', desc: 'Aalitech founded in Hyderabad as the headquarters, marking the beginning of our journey in technology and IT services.' },
    { year: '2025', title: 'Aalitech Guntur Launch', desc: 'Operation office started in Guntur, expanding our reach and capabilities in the region.' },
    { year: '2026', title: 'Aali Conssultancy', desc: 'Strategic expansion into consultancy services - planned establishment.' },
    { year: '2026', title: 'Aali Builders', desc: 'Infrastructure vertical planned with high probability of establishment in 2026.' },
    { year: 'Future', title: 'Future Ventures', desc: 'Aali Gold Jewelz, Aali Music, Aali Filmz, Aali Trustz, Aali Movies - all planned for future launch.' },
  ];

  const values = [
    { title: 'Excellence', desc: 'Striving for the highest standards in everything we do.', icon: 'fas fa-medal', color: '#FF6B35' },
    { title: 'Integrity', desc: 'Transparent, ethical, and accountable at all levels.', icon: 'fas fa-shield-alt', color: '#3B82F6' },
    { title: 'Innovation', desc: 'Embracing change and driving progress.', icon: 'fas fa-lightbulb', color: '#10B981' },
    { title: 'Impact', desc: 'Creating positive change in communities.', icon: 'fas fa-hand-holding-heart', color: '#8B5CF6' },
  ];

  return (
    <>
      {/* HERO SECTION */}
      <section className="about-hero-enhanced">
        <div className="hero-overlay"></div>
        <div className="container hero-content-inner">
          <div className="hero-badge">About Us</div>
          <h1 className="hero-title-main">AALI GROUP</h1>
          <p className="hero-subtitle-main">Building a legacy of excellence across industries with innovation, integrity, and impact.</p>
          <div className="hero-cta-group">
            <Link to="/contact" className="btn-primary hero-btn">Get in Touch →</Link>
            <Link to="/businesses" className="btn-outline-light">Explore Our Companies</Link>
          </div>
        </div>
        <div className="hero-wave-bottom">
          <svg viewBox="0 0 1440 120" preserveAspectRatio="none">
            <path d="M0,64L80,58.7C160,53,320,43,480,48C640,53,800,75,960,80C1120,85,1280,75,1360,69.3L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
          </svg>
        </div>
      </section>

      {/* STORY + STATS - Updated with accurate business facts */}
      <section className="story-stats-enhanced">
        <div className="container">
          <div className="story-stats-grid-enhanced">
            <div className="story-content-enhanced reveal-on-scroll">
              <span className="section-tag">Our Journey</span>
              <h2>Building Tomorrow <span className="highlight">Since 2021</span></h2>
              <p>AALI GROUP is an emerging conglomerate, starting with <strong>Aalitech</strong> in 2021 at Hyderabad. What began as a technology-focused venture has evolved into a vision to create sustainable businesses across multiple sectors.</p>
              <p>Our journey is defined by strategic expansion, operational excellence, and an unwavering commitment to quality. After establishing our headquarters in Hyderabad (2021), we expanded with an operation office in Guntur (2025), and have several exciting ventures planned for the near future.</p>
              <p><strong>Upcoming Ventures:</strong> Aali Conssultancy (2026), Aali Builders (planned 2026), Aali Gold Jewelz, Aali Music, Aali Filmz, Aali Trustz, and Aali Movies - all part of our strategic roadmap to create a diversified ecosystem.</p>
            </div>
            <div className="stats-wrapper-enhanced reveal-on-scroll">
              <div className="stats-grid-enhanced">
                {stats.map((stat, idx) => (
                  <div key={idx} className="stat-card-enhanced">
                    <div className="stat-icon-enhanced" style={{ background: `${stat.color}15`, color: stat.color }}>
                      <i className={stat.icon}></i>
                    </div>
                    <div className="stat-value-enhanced">{stat.value}</div>
                    <div className="stat-label-enhanced">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MILESTONES TIMELINE - Updated with actual and planned dates */}
      <section className="milestones-enhanced">
        <div className="container">
          <div className="section-header-center reveal-on-scroll">
            <span className="section-tag">Timeline</span>
            <h2>Our Journey & Future Roadmap</h2>
            <p>From Aalitech's foundation to an expanding future - our growth story</p>
          </div>
          <div className="timeline-enhanced">
            {milestones.map((m, idx) => (
              <div key={idx} className="timeline-item-enhanced reveal-on-scroll">
                <div className="timeline-dot-enhanced"></div>
                <div className="timeline-content-enhanced">
                  <div className="timeline-year-enhanced">{m.year}</div>
                  <h3>{m.title}</h3>
                  <p>{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* VISION + MISSION */}
      <section className="vision-mission-enhanced">
        <div className="container">
          <div className="vision-mission-grid-enhanced">
            <div className="vm-card-enhanced vision-card-enhanced reveal-on-scroll">
              <div className="vm-icon-enhanced">🎯</div>
              <h3>Our Vision</h3>
              <p>To build a globally recognized conglomerate setting benchmarks for excellence, innovation, and sustainable growth, creating lasting value for stakeholders and positive impact on society.</p>
            </div>
            <div className="vm-card-enhanced mission-card-enhanced reveal-on-scroll">
              <div className="vm-icon-enhanced">🚀</div>
              <h3>Our Mission</h3>
              <p>To deliver exceptional value through innovative solutions, operational excellence, and strategic investments while maintaining the highest standards of governance and social responsibility.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="values-enhanced">
        <div className="container">
          <div className="section-header-center reveal-on-scroll">
            <span className="section-tag">Core Principles</span>
            <h2>Our Core Values</h2>
            <p>The foundation of everything we do</p>
          </div>
          <div className="values-grid-enhanced">
            {values.map((val, idx) => (
              <div key={idx} className="value-card-enhanced reveal-on-scroll">
                <div className="value-icon-enhanced" style={{ background: `${val.color}15`, color: val.color }}>
                  <i className={val.icon}></i>
                </div>
                <h3>{val.title}</h3>
                <p>{val.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* INNOVATION + SOCIAL IMPACT */}
      <section className="impact-innovation-enhanced">
        <div className="container">
          <div className="impact-innovation-grid-enhanced">
            <div className="innovation-card-enhanced reveal-on-scroll">
              <div className="card-icon-enhanced">💡</div>
              <h2>Innovation & Technology</h2>
              <p>Innovation is at the heart of everything we do. Starting with Aalitech's technology foundation, we continuously invest in research and development, adopt cutting-edge technologies, and foster a culture of creativity across all our planned businesses.</p>
              <p>From digital transformation to smart building solutions (Aali Builders planned), we embrace change and drive progress.</p>
              
            </div>
            <div className="impact-card-enhanced reveal-on-scroll">
              <div className="card-icon-enhanced">🌱</div>
              <h2>Social Responsibility</h2>
              <p>Through AALI TRUSTZ (planned future initiative) and across all our companies, we work towards creating sustainable positive impact in education, healthcare, environment, and community development.</p>
              <p>We believe successful businesses have a responsibility to give back to society.</p>
              
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <div className="about-cta-enhanced">
        <div className="container">
          <div className="cta-content-enhanced reveal-on-scroll">
            <h2>Become Part of Our Story</h2>
            <p>Explore opportunities to collaborate, invest, or grow with us</p>
            <div className="cta-buttons-enhanced">
              <Link to="/contact" className="btn-secondary">Get in Touch →</Link>
              <Link to="/careers" className="btn-secondary">Join Our Team</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;